import { computed, inject } from '@angular/core';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { buildTargetQuery, buildTargetSegments } from '@semeris/workspace-ui';
import type { WorkspaceChrome, WorkspaceContext, WorkspaceNav, WorkspacePermissions, WorkspaceTarget } from '@semeris/workspace-ui';

import { wsLink } from '../shared/utils/workspace-link';
import { WorkspaceRecordService } from './workspace-record.service';
import { AppSelectors } from '../../store/app/app.selectors';
import { UserProfileSelectors } from '../../store/user-profile/user-profile.selectors';
import { SemerisRoutes } from '../shared/constants/routes';
import { Permission } from '../shared/models/api/permissions.enum';
import { BreadcrumbService } from '../shared/services/breadcrumb.service';

/**
 * The workspace targets sdocs surfaces. An unmapped kind resolves to a null `href`, which
 * callers read as "the host has no such screen" and fall back on.
 *
 * Add a kind **in the same change that mounts its route**. A kind mapped ahead of its route is
 * worse than an absent one — the fallback disappears and the navigation just fails. Only the
 * mount path lives here: the id segments and query params come from the package
 * (`buildTargetSegments` / `buildTargetQuery`), so every field the screens read rides along.
 */
const ROUTE_BY_KIND: Partial<Record<WorkspaceTarget['kind'], string>> = {
  assistant: SemerisRoutes.agents,
  charts: SemerisRoutes.charts,
  datasets: SemerisRoutes.datasets,
  explorer: SemerisRoutes.datasetExplorer,
  diagrams: SemerisRoutes.diagrams,
  documents: SemerisRoutes.workspaceDocuments,
  reports: SemerisRoutes.reports,
  search: SemerisRoutes.workspaceSearch,
  sheets: SemerisRoutes.sheets,
};

/**
 * The active workspace. `activeId` is the sdocs-selected workspace, whose id is a zing
 * workspace slug — the same value chat already sends to the gateway. `active` is the
 * full record from {@link WorkspaceRecordService}, undefined until that load resolves.
 *
 * Both members are load-bearing: `activeId` builds the chat's stream URLs, while
 * `DatasetApi` reads `active()?.id` — so a context that never hydrates `active` leaves
 * the chat's table renderer querying an empty workspace.
 *
 * Call from a provider factory so `inject()` resolves.
 */
export function sdocsWorkspaceContext(): WorkspaceContext {
  const store = inject(Store);
  const records = inject(WorkspaceRecordService);
  const selectedId = store.selectSignal(AppSelectors.selectedWorkspaceId);

  return {
    activeId: computed(() => selectedId() ?? ''),
    active: records.active,
  };
}

/**
 * Link data for the package's addressable screens — the host's router performs the
 * transition.
 *
 * `link` is path-only, matching every `[routerLink]` in the app; a target's query params
 * ride on `href` alone. The documents screen mirrors its own `href` back into the address
 * bar on every selection, so dropping them there would silently un-address the open
 * document rather than fail.
 *
 * Call from a provider factory so `inject()` resolves.
 */
export function sdocsWorkspaceNav(): WorkspaceNav {
  const router = inject(Router);

  const link = (target: WorkspaceTarget): unknown[] => {
    const route = ROUTE_BY_KIND[target.kind];
    return route ? wsLink(route, ...buildTargetSegments(target)) : [];
  };

  return {
    link,
    href: (target) =>
      ROUTE_BY_KIND[target.kind]
        ? router.serializeUrl(router.createUrlTree(link(target), { queryParams: buildTargetQuery(target) }))
        : null,
  };
}

/**
 * Session authority the package can't derive from the workspace record — per-screen feature
 * gating isn't here, it rides on the record itself via `canAccessRoute(ws, gate)`.
 *
 * Call from a provider factory so `inject()` resolves.
 */
export function sdocsWorkspacePermissions(): WorkspacePermissions {
  const store = inject(Store);
  const profile = store.selectSignal(UserProfileSelectors.profile);

  return {
    isPlatformAdmin: computed(() => profile()?.permissions?.includes(Permission.READ_DOCSTORE_ADMIN) === true),
    canUseAi: computed(() => profile()?.permissions?.includes(Permission.READ_WORKSPACE_AI) === true),
    currentUser: computed(() => {
      const user = profile();
      return user ? { userId: user.userId ?? '', name: user.displayName, email: user.email } : undefined;
    }),
  };
}

/**
 * Write-only breadcrumb sink. sdocs builds its trail from `Route.data.breadcrumb`, so a
 * package screen's segments land on the leaf — the one part route data can't know.
 *
 * Call from a provider factory so `inject()` resolves.
 */
export function sdocsWorkspaceChrome(): WorkspaceChrome {
  const breadcrumbs = inject(BreadcrumbService);

  return {
    setBreadcrumb: (segments) => breadcrumbs.setLeafLabel(segments.at(-1)?.label ?? null),
    clearBreadcrumb: () => breadcrumbs.setLeafLabel(null),
  };
}
